import * as Sentry from '@sentry/browser'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import posthog from 'posthog-js'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { drainQueue } from './lib/media/uploadQueue'
import { routeTree } from './routeTree.gen'

// G1.4: initialise Sentry early so breadcrumbs dropped by SelfieSheet,
// uploadQueue, and other modules during boot have somewhere to land. The DSN
// is read from VITE_SENTRY_DSN — absent in dev, populated in staging/prod via
// the GH Environment secrets. Without a DSN Sentry no-ops, which is fine —
// breadcrumb() wrappers swallow any throws either way.
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE,
    // Default sample rates — production should override via env when traffic warrants.
    tracesSampleRate: 0,
    // Per CLAUDE.md §7: never send OTPs, session tokens, invite codes, photo
    // data URLs, EXIF metadata, or full email addresses. The server-side
    // beforeSend in apps/api/src/observability/sentry.ts owns the manifest;
    // here we keep the surface narrow — no integrations grabbing PII.
  })
}

// CEO-5: PostHog. No DSN → no-op (dev). autocapture is OFF — we fire the
// 7 explicit funnel events from src/lib/analytics.ts so the schema is stable
// across UI refactors. capture_pageview is ON so route hits land for free.
if (import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://app.posthog.com',
    autocapture: false,
    capture_pageview: true,
    persistence: 'localStorage',
  })
}

const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('Root element #root not found in index.html')

createRoot(rootEl).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
)

// F2: drain any media uploads queued in a prior session (mid-upload tab close,
// killed-by-iOS-memory, etc). The queue persists across page loads via
// IndexedDB so a half-finished selfie still lands when the user reopens the
// app. Fire-and-forget; the queue dispatches success/failure events that
// in-stumble routes subscribe to.
void drainQueue()
